import React, { createContext, useCallback, useContext, useEffect, useRef, useState } from 'react';
import axios from 'axios';
import { hasEffectiveModuleAccess } from '../lib/moduleRegistry';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const SESSION_CACHE_KEY = 'zoea-session-snapshot';
const LEGACY_TOKEN_KEY = 'token';
const ACCESS_FALLBACK_KEY = 'zoea-access-fallback';
const REFRESH_BEFORE_EXPIRY_MS = 60 * 1000;
const SESSION_ENDPOINTS_WITHOUT_RETRY = [
  '/auth/login',
  '/auth/register',
  '/auth/refresh',
  '/auth/session',
  '/auth/logout',
];

axios.defaults.withCredentials = true;
axios.defaults.paramsSerializer = (params) => {
  const searchParams = new URLSearchParams();
  Object.entries(params).forEach(([key, value]) => {
    if (value === undefined || value === null) return;
    if (Array.isArray(value)) value.forEach((item) => searchParams.append(key, item));
    else searchParams.append(key, value);
  });
  return searchParams.toString();
};

const readSnapshot = () => {
  try { return JSON.parse(localStorage.getItem(SESSION_CACHE_KEY) || 'null'); }
  catch { return null; }
};

const AuthContext = createContext(null);

const tokenExpiryMs = (accessToken) => {
  try {
    const payload = JSON.parse(atob(accessToken.split('.')[1].replace(/-/g, '+').replace(/_/g, '/')));
    return Number(payload.exp) * 1000;
  } catch {
    return 0;
  }
};

const isSessionEndpointWithoutRetry = (url = '') => (
  SESSION_ENDPOINTS_WITHOUT_RETRY.some((endpoint) => url.includes(endpoint))
);

const wait = (milliseconds) => new Promise((resolve) => window.setTimeout(resolve, milliseconds));

const SESSION_CHANGED_ERROR = 'SESSION_CHANGED_IN_ANOTHER_TAB';

// These frontend routes are intentionally reachable without an application
// session. Keep this matcher aligned with the "Public Routes" section in
// App.js. ProtectedRoute/ReservationsRoute remain the authorization boundary
// for authenticated application pages.
export const PUBLIC_AUTH_PATHS = new Set([
  '/',
  '/pricing',
  '/login',
  '/register',
  '/forgot-password',
  '/reset-password',
  '/accept-invite',
  '/presentatie',
  '/demo',
]);

const PUBLIC_AUTH_PATH_PATTERNS = [
  /^\/book\/[^/]+$/,
  /^\/share\/worship\/[^/]+$/,
  /^\/share\/connectles\/[A-Za-z0-9_-]+$/,
  /^\/shop\/[^/]+(?:\/.*)?$/,
  /^\/[^/]+\/shop(?:\/.*)?$/,
  /^\/[^/]+\/bibliotheek(?:\/.*)?$/,
];

const normalizePathname = (pathname = '') => {
  const value = pathname || '/';
  if (value === '/') return value;
  return value.replace(/\/+$/, '');
};

export const isPublicAuthPath = (pathname = '') => {
  const normalizedPath = normalizePathname(pathname);
  return (
    PUBLIC_AUTH_PATHS.has(normalizedPath)
    || PUBLIC_AUTH_PATH_PATTERNS.some((pattern) => pattern.test(normalizedPath))
  );
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) throw new Error('useAuth must be used within an AuthProvider');
  return context;
};

export const AuthProvider = ({ children }) => {
  const cached = useRef(readSnapshot());
  const tokenRef = useRef(null);
  const refreshPromiseRef = useRef(null);
  const fallbackPromiseRef = useRef(null);
  const authMutationRef = useRef(0);
  const [user, setUser] = useState(null);
  const [token, setToken] = useState(null);
  const [loading, setLoading] = useState(true);
  const [capabilities, setCapabilities] = useState([]);
  const [entitlements, setEntitlements] = useState(null);
  const [sessionOffline, setSessionOffline] = useState(false);

  const setAccessToken = useCallback((accessToken) => {
    tokenRef.current = accessToken || null;
    setToken(accessToken || null);
    if (accessToken) axios.defaults.headers.common.Authorization = `Bearer ${accessToken}`;
    else delete axios.defaults.headers.common.Authorization;
  }, []);

  const persistSnapshot = useCallback((nextUser, nextCapabilities, nextEntitlements) => {
    if (!nextUser) return;
    const snapshot = {
      user: nextUser,
      capabilities: nextCapabilities ?? cached.current?.capabilities ?? [],
      entitlements: nextEntitlements ?? cached.current?.entitlements ?? null,
    };
    cached.current = snapshot;
    localStorage.setItem(SESSION_CACHE_KEY, JSON.stringify(snapshot));
  }, []);

  const clearLocalSession = useCallback(() => {
    localStorage.removeItem(LEGACY_TOKEN_KEY);
    localStorage.removeItem(ACCESS_FALLBACK_KEY);
    localStorage.removeItem(SESSION_CACHE_KEY);
    cached.current = null;
    setAccessToken(null);
    setUser(null);
    setCapabilities([]);
    setEntitlements(null);
    setSessionOffline(false);
  }, [setAccessToken]);

  const applyTokenResponse = useCallback((data, expectedMutation = null) => {
    if (expectedMutation !== null && expectedMutation !== authMutationRef.current) {
      return data.access_token;
    }
    if (
      expectedMutation !== null
      && cached.current?.user?.id
      && data.user?.id
      && cached.current.user.id !== data.user.id
    ) {
      setAccessToken(null);
      setUser(null);
      setCapabilities([]);
      setEntitlements(null);
      setSessionOffline(false);
      cached.current = null;
      throw new Error(SESSION_CHANGED_ERROR);
    }

    setAccessToken(data.access_token);
    // Token endpoints deliberately do not replace the authorization snapshot.
    // effective_access/effective_permissions are authoritative only when they
    // come from /auth/me. Refresh/session/login responses may contain the same
    // UserResponse shape with projection fields at their default-empty values.
    // Writing that raw user here would make access disappear mid-session.
    localStorage.setItem(ACCESS_FALLBACK_KEY, data.access_token);
    setSessionOffline(false);
    return data.access_token;
  }, [setAccessToken]);

  useEffect(() => {
    const onSessionStorageChanged = (event) => {
      if (event.key !== SESSION_CACHE_KEY || !event.newValue) return;
      try {
        const next = JSON.parse(event.newValue);
        const currentUserId = cached.current?.user?.id;
        if (currentUserId && next.user?.id && currentUserId !== next.user.id) {
          // Do not remove shared localStorage here: that would log out the tab
          // that intentionally started the new session. Only this tab resets.
          setAccessToken(null);
          setUser(null);
          setCapabilities([]);
          setEntitlements(null);
          setSessionOffline(false);
          cached.current = null;
        }
      } catch {
        // Ignore malformed storage events; the normal auth request remains authoritative.
      }
    };
    window.addEventListener('storage', onSessionStorageChanged);
    return () => window.removeEventListener('storage', onSessionStorageChanged);
  }, [setAccessToken]);

  const refreshAccessToken = useCallback(async () => {
    if (!refreshPromiseRef.current) {
      const mutationVersion = authMutationRef.current;
      const refreshClient = axios.create({ withCredentials: true });
      refreshPromiseRef.current = (async () => {
        for (let attempt = 0; attempt < 3; attempt += 1) {
          try {
            const response = await refreshClient.post(`${API}/auth/refresh`);
            return applyTokenResponse(response.data, mutationVersion);
          } catch (error) {
            if (error.response?.status !== 409 || attempt === 2) throw error;
            // Another Zoea tab rotated the shared cookie. Give that response
            // time to reach the browser, then retry with the replacement.
            await wait(250 * (attempt + 1));
          }
        }
        return null;
      })()
        .finally(() => { refreshPromiseRef.current = null; });
    }
    return refreshPromiseRef.current;
  }, [applyTokenResponse]);

  // Bridge for browsers that block the Railway refresh cookie as third-party
  // state: exchange the bounded fallback credential (kept fresh by
  // applyTokenResponse on every successful refresh/login) for a fresh access
  // token and try to (re-)set the HttpOnly session. Deduplicated the same way
  // as refreshAccessToken, so concurrent 401s share one exchange attempt
  // instead of racing separate /auth/session calls.
  const exchangeFallbackToken = useCallback(() => {
    if (!fallbackPromiseRef.current) {
      const mutationVersion = authMutationRef.current;
      fallbackPromiseRef.current = (async () => {
        const legacyToken = localStorage.getItem(LEGACY_TOKEN_KEY);
        const fallbackToken = localStorage.getItem(ACCESS_FALLBACK_KEY);
        if (!legacyToken && !fallbackToken) return null;
        const response = await axios.post(`${API}/auth/session`, null, {
          headers: { Authorization: `Bearer ${legacyToken || fallbackToken}` },
        });
        const accessToken = applyTokenResponse(response.data, mutationVersion);
        if (legacyToken) localStorage.removeItem(LEGACY_TOKEN_KEY);
        return accessToken;
      })()
        .finally(() => { fallbackPromiseRef.current = null; });
    }
    return fallbackPromiseRef.current;
  }, [applyTokenResponse]);

  // This used to be a one-time bootstrap-only path (see loadProfile below).
  // That left every OTHER refresh site -- the response interceptor, the
  // pre-expiry timer, the online-reconnect handler -- with no fallback: the
  // first time the cross-site cookie failed mid-session, they cleared the
  // whole session outright instead of trying the bounded credential, which
  // is exactly why a stuck session needed a manual logout/login to recover.
  const refreshAccessTokenWithFallback = useCallback(async () => {
    try {
      return await refreshAccessToken();
    } catch (error) {
      if (error.response?.status !== 401) throw error;
      const accessToken = await exchangeFallbackToken();
      if (!accessToken) throw error;
      return accessToken;
    }
  }, [refreshAccessToken, exchangeFallbackToken]);

  const loadSessionDetails = useCallback(async (expectedMutation = null) => {
    const meResponse = await axios.get(`${API}/auth/me`);
    const optionalGet = (url) => axios.get(url).catch((error) => {
      // These endpoints provide editing/display metadata only. /auth/me is the
      // sole access projection, so an optional 403/404 must not log a user out.
      if ([403, 404].includes(error.response?.status)) return { data: null };
      throw error;
    });
    const [capabilitiesResponse, entitlementsResponse] = await Promise.all([
      optionalGet(`${API}/team/capabilities`),
      optionalGet(`${API}/organization/entitlements`),
    ]);
    if (expectedMutation !== null && expectedMutation !== authMutationRef.current) return meResponse.data;
    // /auth/me is the authentication boundary. Auxiliary capability and
    // entitlement endpoints must not blank the entire app when a legacy or
    // restricted account returns 404/403 for one of them.
    const capabilities = capabilitiesResponse.data || [];
    const entitlements = entitlementsResponse.data || null;
    const userSnapshot = meResponse.data;
    setUser(userSnapshot);
    setCapabilities(capabilities);
    setEntitlements(entitlements);
    persistSnapshot(userSnapshot, capabilities, entitlements);
    return userSnapshot;
  }, [persistSnapshot]);

  useEffect(() => {
    const requestInterceptor = axios.interceptors.request.use((config) => {
      if (tokenRef.current && !config.headers?.Authorization) {
        config.headers = config.headers || {};
        config.headers.Authorization = `Bearer ${tokenRef.current}`;
      }
      return config;
    });
    const responseInterceptor = axios.interceptors.response.use(
      (response) => response,
      async (error) => {
        const config = error.config || {};
        const skipRefreshRetry = isSessionEndpointWithoutRetry(config.url);
        if (error.response?.status !== 401 || config.__zoeaRetried || skipRefreshRetry) throw error;
        config.__zoeaRetried = true;
        try {
          const accessToken = await refreshAccessTokenWithFallback();
          config.headers = config.headers || {};
          config.headers.Authorization = `Bearer ${accessToken}`;
          return axios(config);
        } catch (refreshError) {
          if (refreshError.response?.status === 401) clearLocalSession();
          throw error;
        }
      },
    );
    return () => {
      axios.interceptors.request.eject(requestInterceptor);
      axios.interceptors.response.eject(responseInterceptor);
    };
  }, [clearLocalSession, refreshAccessTokenWithFallback]);

  useEffect(() => {
    if (!token) return undefined;
    const expiresAt = tokenExpiryMs(token);
    if (!expiresAt) return undefined;
    const delay = Math.max(0, expiresAt - Date.now() - REFRESH_BEFORE_EXPIRY_MS);
    const timer = window.setTimeout(() => {
      refreshAccessTokenWithFallback().catch((error) => {
        if (error.response?.status === 401) clearLocalSession();
      });
    }, delay);
    return () => window.clearTimeout(timer);
  }, [clearLocalSession, refreshAccessTokenWithFallback, token]);

  useEffect(() => {
    let active = true;
    const loadProfile = async () => {
      const mutationVersion = authMutationRef.current;
      try {
        await refreshAccessTokenWithFallback();
        if (!active || mutationVersion !== authMutationRef.current) return;
        await loadSessionDetails(mutationVersion);
        if (!active || mutationVersion !== authMutationRef.current) return;
      } catch (error) {
        if (!active) return;
        const unauthorized = [401, 403].includes(error.response?.status);
        if (unauthorized) {
          clearLocalSession();
        } else if (!error.response && cached.current?.user) {
          // Offline/timeouts never destroy a valid remembered session. The UI
          // can reopen with its non-sensitive shell snapshot and retry later.
          setUser(cached.current.user);
          setCapabilities(cached.current.capabilities || []);
          setEntitlements(cached.current.entitlements || null);
          setSessionOffline(true);
        }
        // Do not leave protected routes in a blank shell after an expired or
        // missing session. A hard navigation also recovers from stale router
        // state and service-worker shells in older browser tabs.
        if (unauthorized && !isPublicAuthPath(window.location.pathname)) {
          window.location.replace(`/login?returnTo=${encodeURIComponent(window.location.pathname)}`);
        }
      } finally {
        if (active) setLoading(false);
      }
    };
    loadProfile();
    return () => { active = false; };
  }, [clearLocalSession, loadSessionDetails, refreshAccessTokenWithFallback]);

  useEffect(() => {
    if (!sessionOffline) return undefined;
    const reconnect = async () => {
      try {
        await refreshAccessTokenWithFallback();
        await loadSessionDetails();
        setSessionOffline(false);
      } catch (error) {
        if (error.response?.status === 401) clearLocalSession();
      }
    };
    window.addEventListener('online', reconnect);
    return () => window.removeEventListener('online', reconnect);
  }, [clearLocalSession, loadSessionDetails, refreshAccessTokenWithFallback, sessionOffline]);

  const login = async (email, password) => {
    authMutationRef.current += 1;
    const response = await axios.post(`${API}/auth/login`, { email, password });
    applyTokenResponse(response.data);
    return loadSessionDetails();
  };

  const register = async (email, password, name, organizationName, plan = 'starter') => {
    authMutationRef.current += 1;
    const response = await axios.post(`${API}/auth/register`, {
      email, password, name, organization_name: organizationName, plan,
    });
    applyTokenResponse(response.data);
    return loadSessionDetails();
  };

  const logout = async () => {
    authMutationRef.current += 1;
    try { await axios.post(`${API}/auth/logout`); }
    catch (error) { if (error.response) console.error('Server logout failed:', error); }
    finally { clearLocalSession(); }
  };

  const updateUser = (userData) => {
    setUser(userData);
    persistSnapshot(userData);
  };

  const loginWithToken = async (accessToken, userData) => {
    authMutationRef.current += 1;
    const mutationVersion = authMutationRef.current;

    // Invite/reset endpoints already set the HttpOnly refresh cookie. Their
    // embedded user is authentication metadata only; authorization state is
    // always reloaded from /auth/me before callers choose a landing route.
    setAccessToken(accessToken);
    localStorage.setItem(ACCESS_FALLBACK_KEY, accessToken);
    setSessionOffline(false);

    if (
      cached.current?.user?.id
      && userData?.id
      && cached.current.user.id !== userData.id
    ) {
      setUser(null);
      setCapabilities([]);
      setEntitlements(null);
      cached.current = null;
    }

    return loadSessionDetails(mutationVersion);
  };

  // Record-specific domain rules can still inspect literal roles until those
  // record policies are migrated; module/capability authorization never does.
  const hasRole = (role) => (user?.roles || []).includes(role);
  const hasCapability = (key) => {
    if (Array.isArray(user?.effective_permissions)) {
      return user.effective_permissions.includes(key);
    }

    // Rolling-deploy compatibility only: the immediately previous backend
    // already computes effective_modules but does not yet emit
    // effective_permissions. Keep the old capability semantics bounded to that
    // exact absence. Once the new backend is live, this branch is unreachable
    // and the server projection is the sole source of truth.
    const modulePermissions = user?.module_permissions || {};
    const overrides = user?.permission_overrides || {};

    // Worship switched its UI to V2 names in this release. The previous
    // backend still exposes the three legacy keys and granted grant-access to
    // admin/worshipleader by role. Mirror exactly that old UI behavior only in
    // this rollout fallback.
    const legacyWorshipAliases = {
      'worship.view': 'worship_access',
      'worship.view_all': 'worship_full_view',
      'worship.manage': 'worship_manage',
    };
    const legacyWorshipKey = legacyWorshipAliases[key];
    if (legacyWorshipKey) {
      if (Object.prototype.hasOwnProperty.call(modulePermissions, legacyWorshipKey)) {
        return !!modulePermissions[legacyWorshipKey];
      }
      if (Object.prototype.hasOwnProperty.call(overrides, legacyWorshipKey)) {
        return !!overrides[legacyWorshipKey];
      }
    }
    if (key === 'worship.grant_access') {
      return (user?.roles || []).some((role) => ['admin', 'worshipleader'].includes(role));
    }

    if (Object.prototype.hasOwnProperty.call(modulePermissions, key)) return !!modulePermissions[key];
    if (Object.prototype.hasOwnProperty.call(overrides, key)) return !!overrides[key];

    if (key === 'reservations.view') {
      if (modulePermissions.bookings_approve === true || overrides.bookings_approve === true) return true;
    }
    if (key === 'reservations.approve') {
      if (Object.prototype.hasOwnProperty.call(modulePermissions, 'bookings_approve')) return !!modulePermissions.bookings_approve;
      if (Object.prototype.hasOwnProperty.call(overrides, 'bookings_approve')) return !!overrides.bookings_approve;
    }

    const personalModuleCapabilities = {
      worship_access: 'worship_planning',
      songwriting_access: 'songwriting_studio',
      'reservations.view': 'reserveringen',
      'reservations.create': 'reserveringen',
    };
    const personalGrant = personalModuleCapabilities[key];
    if (personalGrant && (user?.module_access || []).includes(personalGrant)) return true;

    const compatibilityKey = legacyWorshipKey || key;
    const capability = capabilities.find((item) => item.key === compatibilityKey);
    const roles = new Set(user?.roles || []);
    return capability ? (capability.default_roles || []).some((role) => roles.has(role)) : false;
  };
  const hasModule = (moduleKey) => hasEffectiveModuleAccess(user, moduleKey);


  return (
    <AuthContext.Provider value={{
      user, token, loading, login, register, logout, updateUser, loginWithToken,
      isAuthenticated: !!user, hasRole, hasCapability,
      isAdmin: !!user?.is_organization_admin,
      isPlatformAdmin: !!user?.is_platform_admin, entitlements, capabilities, hasModule, sessionOffline,
    }}>
      {children}
    </AuthContext.Provider>
  );
};

export default AuthContext;
